const svg = (body: string): string => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;

export const VIDEO_ICONS = {
  play: svg('<path d="m9 5 11 7-11 7Z"/>'),
  pause: svg('<path d="M9 5v14M15 5v14"/>'),
  muted: svg('<path d="M11 5 6 9H3v6h3l5 4ZM16 9l5 6M21 9l-5 6"/>'),
  sound: svg('<path d="M11 5 6 9H3v6h3l5 4ZM15 8a6 6 0 0 1 0 8M18 5a10 10 0 0 1 0 14"/>'),
  fullscreen: svg('<path d="M8 3H3v5M16 3h5v5M21 16v5h-5M3 16v5h5"/>'),
  restore: svg('<path d="M3 8h5V3M21 8h-5V3M16 21v-5h5M8 21v-5H3"/>'),
} as const;

export function formatVideoTime(seconds: number): string {
  const value = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, "0")}`;
}

interface VideoItem {
  shell: HTMLElement;
  video: HTMLVideoElement;
  play: HTMLButtonElement;
  mute: HTMLButtonElement;
  fullscreen: HTMLButtonElement;
  seek: HTMLInputElement;
  volume: HTMLInputElement | null;
  time: HTMLElement;
  status: HTMLElement;
  wantsPlay: boolean;
  failed: boolean;
  lastVolume: number;
}

/** Owns only the two real media elements, never the page scroll or 3D timeline. */
export class DualVideoPlayerController {
  private readonly items: VideoItem[] = [];
  private readonly cleanup: Array<() => void> = [];
  private active = false;
  private destroyed = false;
  private started = false;
  private epoch = 0;
  private readinessTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly document: Document;
  private fullscreenItem: VideoItem | null = null;
  private fullscreenOrigin: { marker: Comment; scrollX: number; scrollY: number } | null = null;
  private readonly sourceEpochs = new WeakMap<VideoItem, number>();
  private readonly iconStates = new WeakMap<HTMLButtonElement, string>();

  constructor(root: ParentNode) {
    this.document = root.ownerDocument ?? root as Document;
    for (const shell of root.querySelectorAll<HTMLElement>("[data-bb-video-player]")) {
      const video = shell.querySelector<HTMLVideoElement>("video");
      const play = shell.querySelector<HTMLButtonElement>('[data-video-action="play"]');
      const mute = shell.querySelector<HTMLButtonElement>('[data-video-action="mute"]');
      const fullscreen = shell.querySelector<HTMLButtonElement>('[data-video-action="fullscreen"]');
      const seek = shell.querySelector<HTMLInputElement>("[data-video-seek]");
      const volume = shell.querySelector<HTMLInputElement>("[data-video-volume]");
      const time = shell.querySelector<HTMLElement>("[data-video-time]");
      const status = shell.querySelector<HTMLElement>("[data-video-status]");
      if (!video || !play || !mute || !fullscreen || !seek || !time || !status) continue;
      const item: VideoItem = { shell, video, play, mute, fullscreen, seek, volume, time, status, wantsPlay: true, failed: false, lastVolume:1 };
      this.items.push(item);
      video.controls = false;
      video.autoplay = false; // The readiness gate, not the browser, starts the pair.
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      this.listen(play, "click", () => this.togglePlay(item));
      this.listen(video, "click", () => this.togglePlay(item));
      this.listen(shell, "click", event => { if(event.target===shell)this.togglePlay(item); });
      video.tabIndex=0;
      this.listen(video, "keydown", event => {
        if([' ','Enter'].includes((event as KeyboardEvent).key)){event.preventDefault();this.togglePlay(item);}
      });
      if(volume)this.listen(volume,"input",()=>{
        video.volume=Math.max(0,Math.min(1,Number(volume.value)/100));
        video.muted=video.volume===0;
        if(video.volume>0)item.lastVolume=video.volume;
        if(!video.muted)for(const other of this.items)if(other!==item){other.video.muted=true;this.update(other);}
        this.update(item);
      });
      this.listen(mute, "click", () => {
        const shouldUnmute = video.muted;
        if(shouldUnmute&&video.volume===0)video.volume=item.lastVolume;
        for (const other of this.items) {
          other.video.muted = other === item ? !shouldUnmute : true;
          this.update(other);
        }
      });
      this.listen(fullscreen, "click", () => void this.toggleFullscreen(item));
      this.listen(seek, "input", () => {
        if (Number.isFinite(video.duration)) video.currentTime = Number(seek.value) / 1000 * video.duration;
        this.update(item);
      });
      this.listen(video, "canplay", () => {
        item.failed = false;
        this.startTogether();
      });
      this.listen(video, "loadedmetadata", () => this.update(item));
      this.listen(video, "timeupdate", () => this.update(item));
      this.listen(video, "volumechange", () => this.update(item));
      this.listen(video, "play", () => this.update(item));
      this.listen(video, "playing", () => {
        item.shell.dataset.mediaState = "playing";
        item.status.textContent = "";
        this.update(item);
      });
      this.listen(video, "pause", () => this.update(item));
      this.listen(video, "waiting", () => {
        if (this.active && item.wantsPlay) item.shell.dataset.mediaState = "buffering";
      });
      this.listen(video, "error", () => {
        item.failed = true;
        item.shell.dataset.mediaState = "failed";
        item.status.textContent = "Видео не загрузилось";
        this.update(item);
        this.startTogether();
      });
      this.update(item);
    }
    this.listen(this.document, "fullscreenchange", () => {
      if (!this.document.fullscreenElement) this.restoreFullscreenPlayer();
      this.items.forEach(item => this.update(item));
    });
    this.listen(this.document, "visibilitychange", () => {
      if (this.document.hidden) {
        this.epoch += 1;
        this.items.forEach(item => item.video.pause());
      } else if (this.active) {
        this.started = false;
        this.startTogether();
      }
    });
  }

  setActive(active: boolean): void {
    if (this.destroyed || this.active === active) return;
    this.active = active;
    this.epoch += 1;
    this.started = false;
    clearTimeout(this.readinessTimer);
    for (const item of this.items) {
      if (active) {
        item.wantsPlay = true;
        item.video.preload = "auto";
      } else {
        if (!this.isFullscreen(item)) {
          item.video.pause();
          item.video.muted = true;
        }
      }
      this.update(item);
    }
    if (active) {
      this.startTogether();
      // A failed or slow second asset must not trap the healthy first player.
      if (!this.started) this.readinessTimer = setTimeout(() => this.startTogether(true), 8000);
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.setActive(false);
    this.destroyed = true;
    if (this.fullscreenItem) {
      void this.document.exitFullscreen?.().catch(() => {});
      this.restoreFullscreenPlayer();
    }
    for (const item of this.items) item.video.pause();
    clearTimeout(this.readinessTimer);
    for (const remove of this.cleanup) remove();
    this.items.length = 0;
  }

  private listen(target: EventTarget, type: string, listener: EventListener): void {
    target.addEventListener(type, listener);
    this.cleanup.push(() => target.removeEventListener(type, listener));
  }

  private startTogether(allowPartial = false): void {
    if (!this.active || this.destroyed || this.document.hidden) return;
    const available = this.items.filter(item => !item.failed && item.wantsPlay);
    if (!available.length) return;
    if (!this.started && !allowPartial && available.some(item => item.video.readyState < 3)) return;
    const playable = available.filter(item => item.video.readyState >= (allowPartial ? 2 : 3));
    if (!playable.length) return;
    this.started = true;
    clearTimeout(this.readinessTimer);
    // Both play() calls are issued in the same task, after both files can play.
    for (const item of playable) if (item.video.paused) void this.play(item);
  }

  private async play(item: VideoItem): Promise<void> {
    const epoch = this.epoch;
    try {
      await item.video.play();
      if (this.destroyed || this.document.hidden || ((!this.active || epoch !== this.epoch) && !this.isFullscreen(item))) item.video.pause();
    } catch {
      if (!this.active || this.destroyed || epoch !== this.epoch) return;
      item.shell.dataset.mediaState = "paused";
      item.wantsPlay = false;
      item.status.textContent = "";
    }
    if (!this.destroyed) this.update(item);
  }

  private togglePlay(item: VideoItem): void {
    if (item.failed) {
      item.failed = false;
      item.wantsPlay = true;
      item.status.textContent = "";
      item.shell.dataset.mediaState = "buffering";
      item.video.load();
      return;
    }
    item.wantsPlay = item.video.paused;
    if (item.wantsPlay) void this.play(item);
    else item.video.pause();
    this.update(item);
  }

  private async toggleFullscreen(item: VideoItem): Promise<void> {
    try {
      if (this.document.fullscreenElement === item.shell) await this.document.exitFullscreen();
      else if (item.shell.requestFullscreen) {
        // Fullscreen must not inherit visibility from a scroll scene. Browser
        // fullscreen changes viewport and scroll before our next render frame.
        const view = this.document.defaultView;
        const marker = this.document.createComment("video fullscreen return");
        item.shell.before(marker);
        this.fullscreenOrigin = { marker, scrollX: view?.scrollX ?? 0, scrollY: view?.scrollY ?? 0 };
        this.fullscreenItem = item;
        item.shell.dataset.videoFullscreenPortal = "true";
        this.document.body.append(item.shell);
        await item.shell.requestFullscreen();
        if (item.video.dataset.fullscreenSrc) void this.selectSource(item, item.video.dataset.fullscreenSrc);
      }
      else (item.video as HTMLVideoElement & { webkitEnterFullscreen?: () => void }).webkitEnterFullscreen?.();
    } catch {
      this.restoreFullscreenPlayer();
    }
    this.update(item);
  }

  private isFullscreen(item: VideoItem): boolean {
    return this.fullscreenItem === item || this.document.fullscreenElement === item.shell;
  }

  private restoreFullscreenPlayer(): void {
    const item = this.fullscreenItem;
    const origin = this.fullscreenOrigin;
    if (!item || !origin) return;
    this.fullscreenItem = null;
    this.fullscreenOrigin = null;
    origin.marker.replaceWith(item.shell);
    delete item.shell.dataset.videoFullscreenPortal;
    const view = this.document.defaultView;
    view?.scrollTo({ left: origin.scrollX, top: origin.scrollY, behavior: "instant" });
    if (!this.destroyed && item.video.dataset.embeddedSrc) void this.selectSource(item, item.video.dataset.embeddedSrc);
    item.fullscreen.focus({ preventScroll: true });
  }

  private async selectSource(item: VideoItem, src: string): Promise<void> {
    const video = item.video;
    if (video.getAttribute("src") === src) return;
    const sourceEpoch = (this.sourceEpochs.get(item) ?? 0) + 1;
    this.sourceEpochs.set(item, sourceEpoch);
    const time = video.currentTime;
    const resume = item.wantsPlay;
    const previousPoster = video.poster;
    // Hold this actual decoded frame while a different resolution seeks to it.
    const canvas = this.document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    try {
      canvas.getContext("2d")?.drawImage(video, 0, 0);
      video.poster = canvas.toDataURL("image/jpeg", 0.92);
    } catch { /* The ordinary video poster remains valid if capture is denied. */ }
    video.pause();
    video.src = src;
    video.load();
    try {
      await new Promise<void>((resolve, reject) => {
        const finish = (error = false) => {
          clearTimeout(timeout);
          video.removeEventListener("loadedmetadata", metadata);
          video.removeEventListener("seeked", seeked);
          video.removeEventListener("error", failed);
          error ? reject(new Error("Video source unavailable")) : resolve();
        };
        const seeked = () => finish();
        const failed = () => finish(true);
        const metadata = () => {
          video.currentTime = Math.min(time, Math.max(0, video.duration - .01));
          if (time === 0) finish();
        };
        const timeout = setTimeout(() => finish(true), 10000);
        video.addEventListener("loadedmetadata", metadata, { once: true });
        video.addEventListener("seeked", seeked, { once: true });
        video.addEventListener("error", failed, { once: true });
      });
      if (this.destroyed || this.sourceEpochs.get(item) !== sourceEpoch) return;
      video.poster = previousPoster;
      if (resume && (this.active || this.isFullscreen(item))) await this.play(item);
    } catch {
      if (!this.destroyed && this.sourceEpochs.get(item) === sourceEpoch) item.status.textContent = "Видео не загрузилось";
    }
  }

  private icon(button: HTMLButtonElement, markup: string): void {
    if (this.iconStates.get(button) === markup) return;
    this.iconStates.set(button, markup);
    button.innerHTML = markup;
  }

  private update(item: VideoItem): void {
    const paused = item.video.paused;
    item.shell.dataset.paused = String(paused);
    this.icon(item.play, paused ? VIDEO_ICONS.play : VIDEO_ICONS.pause);
    item.play.setAttribute("aria-label", item.failed ? "Повторить загрузку видео" : paused ? "Воспроизвести" : "Пауза");
    if(item.volume){item.volume.value=String(item.video.muted?0:Math.round(item.video.volume*100));item.volume.setAttribute('aria-valuetext',`${item.volume.value}%`);}
    this.icon(item.mute, item.video.muted ? VIDEO_ICONS.muted : VIDEO_ICONS.sound);
    item.mute.setAttribute("aria-label", item.video.muted ? "Включить звук" : "Выключить звук");
    item.mute.setAttribute("aria-pressed", String(!item.video.muted));
    const fullscreen = this.document.fullscreenElement === item.shell;
    this.icon(item.fullscreen, fullscreen ? VIDEO_ICONS.restore : VIDEO_ICONS.fullscreen);
    item.fullscreen.setAttribute("aria-label", fullscreen ? "Выйти из полноэкранного режима" : "На весь экран");
    const duration = Number.isFinite(item.video.duration) ? item.video.duration : 0;
    item.seek.disabled = duration <= 0 || item.failed;
    item.seek.value = String(duration ? Math.round(item.video.currentTime / duration * 1000) : 0);
    item.seek.setAttribute("aria-valuetext", `${formatVideoTime(item.video.currentTime)} из ${formatVideoTime(duration)}`);
    item.time.textContent = `${formatVideoTime(item.video.currentTime)} / ${formatVideoTime(duration)}`;
  }
}
